import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { FileDropzoneComponent } from '@shared/components/file-dropzone/file-dropzone.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraAnexoService } from '@shared/services/apis/compras/compra-anexo.service';
import type { Person } from '../../../processo-compras.models';
import {
  avatarColorFromSeed,
  CardAttachment,
  formatBytes,
  KIND_META,
  kindFromName,
} from '../card-edit-dialog.types';

/** Painel de anexos do card (dropzone + lista de arquivos). */
@Component({
  selector: 'app-ced-anexos',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FileDropzoneComponent,
  ],
  templateUrl: './ced-anexos.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-anexos.component.css'],
})
export class CedAnexosComponent {
  public readonly tiposArquivoAceitos = '.pdf,.xlsx,.docx,.csv,.txt,.png,.jpg,.jpeg';
  private readonly extensoesArquivoAceitas = new Set(['pdf', 'xlsx', 'docx', 'csv', 'txt', 'png', 'jpg', 'jpeg']);
  private readonly tamanhoMaximoBytes = 10 * 1024 * 1024;
  @Input() idCompra?: number | null;
  @Input() attachments: CardAttachment[] = [];
  @Input() usersById?: Map<number, Person>;
  @Input() currentPerson?: Person;
  /** Compra encerrada (finalizada, cancelada ou cotação arquivada): não anexa nem remove. */
  @Input() isSomenteLeitura = false;

  /** Emitido sempre que a lista de anexos muda (upload/remoção) — o shell é o dono do array. */
  @Output() attachmentsChange = new EventEmitter<CardAttachment[]>();

  uploadingAnexo = false;

  constructor(
    private compraAnexoService: CompraAnexoService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {}

  processFiles(files: FileList): void {
    if (!this.idCompra || this.isSomenteLeitura) return;
    Array.from(files).forEach(file => {
      if (!this.tipoArquivoPermitido(file)) {
        this.alertService.show('Atenção', 'Selecione um arquivo PDF, XLSX, DOCX, CSV, TXT, PNG ou JPG.', AlertType.warning);
        return;
      }
      if (file.size > this.tamanhoMaximoBytes) {
        this.alertService.show('Atenção', `O arquivo ${file.name} excede o limite de 10 MB.`, AlertType.warning);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        this.uploadingAnexo = true;
        this.compraAnexoService.uploadAnexo(this.idCompra!, base64, file.name, file.size).subscribe({
          next: (res) => {
            this.uploadingAnexo = false;
            if (res.isError) {
              this.alertService.show('Erro', res.errorDescription || 'Não foi possível enviar o arquivo.', AlertType.error);
              return;
            }
            if (res.anexo) {
              const uploader = this.usersById?.get(res.anexo.idUsuario) || this.currentPerson;
              this.attachments = [{
                name: res.anexo.nomeArquivo,
                size: formatBytes(res.anexo.tamanhoBytes),
                kind: kindFromName(res.anexo.nomeArquivo),
                who: uploader?.initials || '',
                whoName: uploader?.name || String(res.anexo.idUsuario),
                whoColor: uploader?.color || avatarColorFromSeed(String(res.anexo.idUsuario)),
                when: new Date(res.anexo.datetimeInclusao).toLocaleDateString('pt-BR'),
                url: res.anexo.urlArquivo,
                idAnexo: res.anexo.idCompraAnexo,
              }, ...this.attachments];
              this.attachmentsChange.emit(this.attachments);
            }
          },
          error: () => {
            this.uploadingAnexo = false;
            this.alertService.show('Erro', 'Não foi possível enviar o arquivo.', AlertType.error);
          },
        });
      };
      reader.readAsDataURL(file);
    });
  }

  private tipoArquivoPermitido(file: File): boolean {
    const extensao = file.name.split('.').pop()?.toLowerCase() ?? '';
    return this.extensoesArquivoAceitas.has(extensao);
  }

  downloadAnexo(a: CardAttachment): void {
    if (!a.idAnexo || !this.idCompra) return;
    this.compraAnexoService.getUrlAnexo(this.idCompra, a.idAnexo).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível baixar o arquivo.', AlertType.error);
          return;
        }
        if (res.urlArquivo) window.open(res.urlArquivo, '_blank');
      },
      error: () => this.alertService.show('Erro', 'Não foi possível baixar o arquivo.', AlertType.error),
    });
  }

  deleteAnexo(a: CardAttachment): void {
    if (!a.idAnexo || !this.idCompra || this.isSomenteLeitura) return;

    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Remover anexo',
        description: `Tem certeza que deseja remover o anexo ${a.name}?`,
        buttonConfirm: 'Remover',
        buttonClose: 'Cancelar',
      },
    });

    ref.afterClosed().subscribe(result => {
      if (!result?.confirm || !this.idCompra) return;

      this.compraAnexoService.deleteAnexo(this.idCompra, a.idAnexo!).subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível remover o arquivo.', AlertType.error);
            return;
          }
          this.attachments = this.attachments.filter(x => x.idAnexo !== a.idAnexo);
          this.attachmentsChange.emit(this.attachments);
          this.alertService.show('Sucesso', 'Anexo removido.', AlertType.success);
        },
        error: () => this.alertService.show('Erro', 'Não foi possível remover o arquivo.', AlertType.error),
      });
    });
  }

  kindMeta(kind: string) {
    return KIND_META[kind] || KIND_META['doc'];
  }
}
